import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Users, Globe, TrendingUp, Award, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const { t } = useLanguage();

  const values = [
    { icon: TrendingUp, title: 'Growth', desc: 'We grow with our users' },
    { icon: Award, title: 'Trust', desc: 'Building lasting relationships' },
    { icon: Heart, title: 'Unity', desc: 'Growing together, hand in hand' },
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="heading-1 mb-6">{t('about.title')}</h1>
            <p className="heading-3 text-gray-600 mb-4">{t('about.subtitle')}</p>
            <p className="text-lg text-gray-600">{t('about.description')}</p>
          </div>

          {/* Founder Image Placeholder */}
          <div className="mt-12 flex justify-center">
            <div
              className="relative"
              style={{
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.3)',
              }}
            >
              <Users className="h-20 w-20 text-white" />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center">
              <Card className="product-card max-w-md">
                <CardContent className="p-8 text-center">
                  {/* Founder Image */}
                  <div className="flex justify-center mb-6">
                    <div
                      className="relative"
                      style={{
                        width: '160px',
                        height: '160px',
                      }}
                    >
                      <div
                        className="relative rounded-2xl overflow-hidden"
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
                        }}
                      >
                        <img
                          src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/aiasb3kz_card%20disign.jpeg"
                          alt="Sameer Rais Ahmad"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '50%',
                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Founder Info */}
                  <h3 className="text-2xl font-bold mb-1" style={{ color: '#10B981' }}>
                    Sameer Rais Ahmad
                  </h3>
                  <p className="text-lg text-gray-600 mb-4">Founder & CEO — Roya Business</p>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Building the next generation of financial infrastructure for Saudi Arabia
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 text-center mb-12">Our Journey</h2>

            <div className="space-y-6">
              {t('about.story').map((point, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-gradient-to-r from-green-50 to-yellow-50 rounded-xl">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-lg">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">{t('about.vision')}</h2>
            <p className="text-3xl font-bold" style={{ color: '#10B981' }}>{t('about.visionText')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <Card key={index} className="product-card">
                <CardContent className="p-8 text-center">
                  <div
                    className="inline-flex p-4 rounded-full mb-4"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    <value.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-800">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-center mb-12">Our Impact</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-gradient-to-br from-green-50 to-yellow-50 rounded-2xl">
                <div className="text-5xl font-bold mb-2" style={{ color: '#10B981' }}>10+</div>
                <div className="text-gray-600 font-medium">Years Experience</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-green-50 to-yellow-50 rounded-2xl">
                <div className="text-5xl font-bold mb-2" style={{ color: '#10B981' }}>1000+</div>
                <div className="text-gray-600 font-medium">Merchants Served</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-green-50 to-yellow-50 rounded-2xl">
                <div className="text-5xl font-bold mb-2" style={{ color: '#10B981' }}>24/7</div>
                <div className="text-gray-600 font-medium">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision 2030 Alignment */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Target className="h-16 w-16 mx-auto mb-6" style={{ color: '#10B981' }} />
            <h2 className="heading-2 mb-6">Aligned with Vision 2030</h2>
            <p className="text-lg text-gray-700 mb-4 font-semibold" style={{ color: '#1B9E4B' }}>
              ROYA is not just a fintech application — it is a complete Saudi financial ecosystem designed to integrate payments, wallets, POS, QR, business tools, and merchant services under one unified platform.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Born in Saudi Arabia, for Saudi Arabia. Our mission is to accelerate the Kingdom&apos;s digital transformation by bringing smart financial solutions to every merchant and individual across the nation, fully supporting Vision 2030&apos;s cashless economy goals.
            </p>
            <div className="flex justify-center items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/1tnawu9j_riyal%20logo.jpeg"
                alt="Saudi Riyal"
                className="w-12 h-12"
              />
              <div className="text-left">
                <div className="font-bold text-xl" style={{ color: '#10B981' }}>Proudly Saudi-Born</div>
                <div className="text-gray-600">Supporting the Kingdom&apos;s digital future</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
